import { industries } from '@/data/discovery/industries';
import { useDiscovery } from '../context/DiscoveryContext';
import { IndustryCategory } from '@/types/discovery';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import { trackEvent } from '@/lib/ga4';

interface IndustryCardProps {
  industry: IndustryCategory;
  onClick: () => void;
}

const IndustryCard = ({ industry, onClick }: IndustryCardProps) => {
  const Icon = industry.icon;
  const hasCustomIcon = !!industry.iconPath;

  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative flex items-center gap-4 p-4 sm:p-5 rounded-2xl',
        'bg-white/10 backdrop-blur-sm border border-white/20',
        'hover:bg-white/20 hover:border-white/40 hover:scale-[1.02] transition-all duration-300',
        'text-left w-full'
      )}
    >
      {/* Icon */}
      <div className="p-3 bg-white/10 rounded-xl flex-shrink-0 group-hover:bg-white/20 transition-colors">
        {hasCustomIcon ? (
          <img src={industry.iconPath} alt={industry.name} className="w-8 h-8 object-contain" />
        ) : (
          <Icon className="w-8 h-8 text-white" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="text-base sm:text-lg font-bold text-white leading-tight">
          {industry.name}
        </h3>
        <p className="text-xs sm:text-sm text-white/70 mt-0.5 leading-snug">
          {industry.description}
        </p>
      </div>

      {/* Arrow */}
      <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
    </button>
  );
};

export const IndustrySelectStep = () => {
  const { selectIndustry } = useDiscovery();

  return (
    <div className="text-center">
      {/* Header */}
      <div className="mb-5 sm:mb-10">
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
          What industry are you in?
        </h1>
        <p className="text-base sm:text-xl text-white/70">
          Select your industry to see voice genies tailored for your business.
        </p>
      </div>

      {/* Industry grid - 2 columns on mobile, 3 on tablet/desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 max-w-5xl mx-auto auto-rows-fr">
        {industries.map(industry => (
          <IndustryCard
            key={industry.id}
            industry={industry}
            onClick={() => {
              trackEvent('discover_industry_selected', { industry: industry.id });
              selectIndustry(industry);
            }}
          />
        ))}
      </div>
    </div>
  );
};
